import isEmpty from 'lodash/isEmpty'
import { InlineInput } from '@ztechapps/webui.input'
import type { InlineInputUpdateParamsFunction, InlineInputValidateFunction } from '@ztechapps/webui.input'
import { useContext as useChatContext } from '../Chat/Context'
import { useChannelContext } from '../Channel/Context'
import { ChatMessages } from '../Chat/ChatMessages'
import { StagedAttachments } from '../Chat/StagedAttachments'
import { useConversationContext } from '../Conversation/Context'
import { useInboxComposerContext } from './Context'
import { updateChatSubjectMutation } from './graphql'
import styles from './InboxThreadLayout.module.scss'

// The title's display row, editor params and guard live at module scope — inline closures would be
// re-created on every render.
const subjectDisplayContent = (value: string) => <h4 className="mb-0 fw-semibold text-break">{value}</h4>

// The mutation args type carries the subject in `input` and the conversation id in `filter` —
// `input` holds no `_id`.
const subjectUpdateParams: InlineInputUpdateParamsFunction = ({ _id, updatedValue }) => ({
  input: { subject: updatedValue },
  filter: { _id },
})

const validateSubject: InlineInputValidateFunction = (value) => {
  if (isEmpty(value?.trim())) throw new Error('The subject cannot be empty.')
}

// The inbox's `components.layout` override (Chat/chatComponents.tsx → `layout`, supplied
// through ConversationDetail.tsx's `components`): the message region takes the free space and
// scrolls while the bare composer stays put beneath it, unlike every other Channel mount's
// DefaultLayout (composer inline, one action row, then the thread).
export const InboxThreadLayout = () => {
  const { components, ...props } = useChatContext()
  const { attachments, removeAttachment, status } = useChannelContext()
  const { subject, documentId, hasRole: canEdit } = useConversationContext()
  const { titleRef } = useInboxComposerContext()

  // View-only mounts swap the composer for NullComponent (Conversation/Context.tsx) — they
  // keep today's empty region instead of gaining an empty wrapper.
  const composer = (
    <>
      <components.input />
      <StagedAttachments attachments={attachments} onRemove={removeAttachment} disabled={status?.busy} />
      <components.chatActionContainer {...props} />
    </>
  )

  return (
    <div className="d-flex flex-column h-100">
      {/* COUPLING: this row's `pb-2`, `border-bottom` and `mb-2` are counted in COMPOSER_THREAD_CHROME_HEIGHT in libs/chat/webui/src/Inbox/constants.ts; its content height is measured (titleHeight) instead. */}
      <div ref={titleRef} className="flex-shrink-0 border-bottom pb-2 mb-2">
        <InlineInput
          name="subject"
          canEdit={canEdit}
          initialValues={{ _id: documentId, subject }}
          mutationConfig={{ query: updateChatSubjectMutation }}
          updateParams={subjectUpdateParams}
          validate={validateSubject}
          displayContent={subjectDisplayContent}
        />
      </div>

      {/* COUPLING: this wrapper's `mb-1` (4px), around an error display that renders an empty `div` with no
          error, is counted in COMPOSER_THREAD_CHROME_HEIGHT in libs/chat/webui/src/Inbox/constants.ts. */}
      <div className="mb-1">
        <components.errorDisplay {...props} />
      </div>

      <div className={`thread-messages flex-fill overflow-auto ${styles.threadMessages}`}>
        <ChatMessages />
      </div>

      {/* The message region is the thread's only scroller and the composer section sits
          outside it, bare, so the channel-owned draft survives every render. */}
      <div className="thread-composer bg-body flex-shrink-0">{composer}</div>
    </div>
  )
}
